import { useCallback } from 'react';
import { driver, DriveStep } from 'driver.js';
import 'driver.js/dist/driver.css';
import '../styles/tour.css';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useEvent } from '../contexts/EventContext';
import { buildNavSections } from '../components/nav/useNavSections';

/** Flag localStorage : tour déjà vu (évite le re-lancement auto au boot). */
export const TOUR_DONE_KEY = 'logfit_tour_completed_v1';

interface TourStepDef {
  /** Route à atteindre avant d'afficher l'étape (react-router). */
  path?: string;
  /** Sélecteur de la zone à mettre en valeur. Omis = popover centré. */
  element?: string;
  titleKey: string;
  descKey: string;
}

/** Avant le menu de l'événement : l'accueil, le sélecteur, la liste. */
const TOUR_DEBUT: TourStepDef[] = [
  { path: '/admin/events', titleKey: 'tour.welcome.title', descKey: 'tour.welcome.desc' },
  { path: '/admin/events', element: '[data-tour="event-selector"]', titleKey: 'tour.eventSelector.title', descKey: 'tour.eventSelector.desc' },
  { path: '/admin/events', element: '[data-tour="nav-events"]', titleKey: 'tour.events.title', descKey: 'tour.events.desc' },
];

/** Après : ce qui ne dépend pas de l'événement. */
const TOUR_FIN: TourStepDef[] = [
  { element: '[data-tour="command-palette"]', titleKey: 'tour.commandPalette.title', descKey: 'tour.commandPalette.desc' },
  { element: '[data-tour="account-menu"]', titleKey: 'tour.settings.title', descKey: 'tour.settings.desc' },
  { element: '[data-tour="help-button"]', titleKey: 'tour.help.title', descKey: 'tour.help.desc' },
];

/**
 * Le menu de l'événement, entrée par entrée, dans l'ordre de la barre
 * latérale. Construit depuis `buildNavSections`, la définition du menu : une
 * entrée ajoutée au menu entre dans le tour (il lui faut seulement ses deux
 * textes `tour.<clé>`). Jusqu'au 2026-09-30 la liste était écrite à la main
 * et s'arrêtait au planning : bénévoles, messages, planning des juges,
 * résultats et les écrans course n'y figuraient pas.
 */
function etapesDuMenu(event: { id: string; event_type?: string | null }, t: (k: string) => string): TourStepDef[] {
  return buildNavSections(event, t).flatMap((section) => section.items.map((item) => {
    // `tour.settings` désigne déjà le menu du compte.
    const cle = item.key === 'settings' ? 'eventSettings' : item.key;
    return {
      path: item.path,
      element: `[data-tour="nav-${item.key}"]`,
      titleKey: `tour.${cle}.title`,
      descKey: `tour.${cle}.desc`,
    };
  }));
}

/** Attend qu'un élément DOM apparaisse (après une navigation react-router). */
function waitForElement(selector: string | undefined, timeout = 4000): Promise<void> {
  if (!selector) return Promise.resolve();
  return new Promise((resolve) => {
    if (document.querySelector(selector)) return resolve();
    const start = Date.now();
    const id = window.setInterval(() => {
      if (document.querySelector(selector) || Date.now() - start > timeout) {
        window.clearInterval(id);
        resolve();
      }
    }, 80);
  });
}

/** Attend que react-router ait réellement commité la navigation vers `path`. */
function waitForPath(path: string, timeout = 4000): Promise<void> {
  return new Promise((resolve) => {
    if (window.location.pathname === path) return resolve();
    const start = Date.now();
    const id = window.setInterval(() => {
      if (window.location.pathname === path || Date.now() - start > timeout) {
        window.clearInterval(id);
        resolve();
      }
    }, 50);
  });
}

/** Deux rAF : laisse React peindre la nouvelle page avant que driver.js mesure le spotlight. */
function nextPaint(): Promise<void> {
  return new Promise((resolve) =>
    requestAnimationFrame(() => requestAnimationFrame(() => resolve()))
  );
}

export function useProductTour() {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { events, selectedEventId, setSelectedEventId } = useEvent();

  const startTour = useCallback(() => {
    // Le parcours événement n'a de sens qu'avec un événement sélectionné.
    // S'il n'y en a pas, on en sélectionne un (sinon on saute ces étapes).
    let event = events.find((e) => e.id === selectedEventId);
    if (!event && events.length > 0) {
      event = events[0];
      setSelectedEventId(event.id);
    }

    const steps = [...TOUR_DEBUT, ...(event ? etapesDuMenu(event, t) : []), ...TOUR_FIN];

    const driveSteps: DriveStep[] = steps.map((s) => ({
      element: s.element,
      popover: {
        title: t(s.titleKey),
        description: t(s.descKey),
      },
    }));

    const markDone = () => {
      try {
        localStorage.setItem(TOUR_DONE_KEY, '1');
      } catch {
        /* localStorage indisponible — pas bloquant */
      }
    };

    const goToStep = async (index: number) => {
      const step = steps[index];
      if (!step) return;
      const targetPath = step.path;
      if (targetPath && window.location.pathname !== targetPath) {
        navigate(targetPath);
        // Attendre que la route soit commitée ET que la page soit peinte avant
        // que driver.js (moveNext, appelé juste après) mesure/cible le spotlight.
        // Sans ça, le spotlight se calcule sur un DOM transitoire → card sur fond
        // noir sans élément mis en valeur (bug des étapes qui naviguent).
        await waitForPath(targetPath);
        await nextPaint();
      }
      await waitForElement(step.element);
    };

    const driverObj = driver({
      showProgress: true,
      animate: !window.matchMedia('(prefers-reduced-motion: reduce)').matches,
      allowClose: true,
      overlayColor: '#000000',
      overlayOpacity: 0.7,
      stagePadding: 6,
      stageRadius: 8,
      popoverClass: 'logfit-tour',
      progressText: t('tour.progress'),
      nextBtnText: t('tour.next'),
      prevBtnText: t('tour.prev'),
      doneBtnText: t('tour.done'),
      steps: driveSteps,
      onNextClick: async () => {
        const current = driverObj.getActiveIndex() ?? 0;
        const nextIndex = current + 1;
        if (nextIndex >= steps.length) {
          markDone();
          driverObj.destroy();
          return;
        }
        await goToStep(nextIndex);
        driverObj.moveNext();
      },
      onPrevClick: async () => {
        const current = driverObj.getActiveIndex() ?? 0;
        const prevIndex = current - 1;
        if (prevIndex < 0) return;
        await goToStep(prevIndex);
        driverObj.movePrevious();
      },
      onDestroyed: () => {
        markDone();
      },
    });

    // Positionne d'abord la 1re page, puis démarre une fois le DOM prêt.
    goToStep(0).then(() => driverObj.drive());
  }, [navigate, t, i18n, events, selectedEventId, setSelectedEventId]);

  return { startTour };
}
